import type { ApolloCache, DataProxy } from '@apollo/client';

import type {
  AdsPageAdFragment,
  CreateAdsPageAdMutation,
  GetSavedAdsQuery,
  GetSavedAdsQueryVariables,
  TargetingTermValue,
} from '../../../../../apis/graphql';
import { campaignClient } from '../../../../../apis/graphql';
import type { AdFormValues } from '../../../../../common/AdForm/adFormik';
import {
  computeHasInvalidCountryForAdSave,
  computeHasInvalidPublisherForAdSave,
  getAllCountryCodesMentionedInAdTargeting,
  getCountriesTargetFromAdTargetingSubmission,
  getDisplayPublisherTargetAfterAdTargetingSave,
} from '../../../../../common/adTargeting';
import { getEarliestDateRange, getLatestDateRange } from '../../../../../common/utils';
import type { TargetingTermValue as TargetingTermValueModel } from '../../../../../models';
import { GET_SAVED_ADS } from '../../queries';

/** Country code lists in cached ads are sorted alphabetically (CampaignSearcher / UI parity). */
export function alphaSortCountryCodes(values: null): null;
export function alphaSortCountryCodes(values: undefined): undefined;
export function alphaSortCountryCodes(values: string[]): string[];
export function alphaSortCountryCodes(values: string[] | null | undefined): string[] | null | undefined {
  if (values == null) return values;
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function effectiveLineItemCountriesFromAdNode(
  createAdNode: NonNullable<CreateAdsPageAdMutation['createAd']>,
  lineItemCountriesFromCaller: string[]
): string[] {
  const fromMutationLineItem = createAdNode.lineItem?.countries?.filter(Boolean);
  if (fromMutationLineItem?.length) return fromMutationLineItem;

  return lineItemCountriesFromCaller ?? [];
}

function effectiveLineItemDisplayPublishersFromAdNode(
  createAdNode: NonNullable<CreateAdsPageAdMutation['createAd']>,
  lineItemDisplayPublisherTargetFromCaller: string[]
): string[] {
  const fromMutationLineItem = createAdNode.lineItem?.displayPublisherTarget?.filter(Boolean);
  if (fromMutationLineItem?.length) return fromMutationLineItem;

  return lineItemDisplayPublisherTargetFromCaller ?? [];
}

/**
 * Appends a newly created ad to the local `getSavedAds` connection with CampaignSearcher-style
 * embellishment fields. When the mutation response already includes embellishments (and nested
 * `lineItem` constraint fields), those values are preferred so the cache matches a network
 * response; local recompute fills gaps when fields are null/undefined (e.g. stale consumer paths).
 *
 * @returns The normalized node written into `edges[0].node` (same shape Trafficking should use for `adsV5` edges).
 */
export function appendCreatedAdToSavedAdsCache(
  cache: ApolloCache<object>,
  lineItemId: string,
  adFormValues: AdFormValues,
  createAdNode: NonNullable<CreateAdsPageAdMutation['createAd']>,
  lineItemCountries: string[] = [],
  selectedAdIds?: string[],
  lineItemDisplayPublisherTarget: string[] = []
): AdsPageAdFragment {
  const currentAds = cache.readQuery<GetSavedAdsQuery, GetSavedAdsQueryVariables>({
    query: GET_SAVED_ADS,
    variables: {
      lineItemIds: [lineItemId],
      lineItemId,
      ...(selectedAdIds && { selectedAdIds }),
    },
  });

  const newAdSchedule = createAdNode.schedule;

  const targetingTermValues = createAdNode.targetingTermValues.length
    ? createAdNode.targetingTermValues
    : (adFormValues.targetingTermValues as TargetingTermValue[]);

  const termsForCompute = targetingTermValues as TargetingTermValueModel[];

  const effectiveLineItemCountries = effectiveLineItemCountriesFromAdNode(createAdNode, lineItemCountries);
  const effectiveLineItemPublishers = effectiveLineItemDisplayPublishersFromAdNode(
    createAdNode,
    lineItemDisplayPublisherTarget
  );

  const recomputedCountriesTarget = getCountriesTargetFromAdTargetingSubmission(
    termsForCompute,
    effectiveLineItemCountries
  );
  const countriesTargetRaw = createAdNode.countriesTarget ?? recomputedCountriesTarget;
  const countriesTarget = alphaSortCountryCodes(countriesTargetRaw) ?? countriesTargetRaw;

  const recomputedHasInvalidCountry = computeHasInvalidCountryForAdSave(
    getAllCountryCodesMentionedInAdTargeting(termsForCompute),
    effectiveLineItemCountries
  );
  const hasInvalidCountry = createAdNode.hasInvalidCountry ?? recomputedHasInvalidCountry;

  const recomputedDisplayPublisherTarget = getDisplayPublisherTargetAfterAdTargetingSave(
    termsForCompute,
    effectiveLineItemPublishers,
    createAdNode.displayPublisherTarget ?? []
  );
  const displayPublisherTarget = createAdNode.displayPublisherTarget ?? recomputedDisplayPublisherTarget;

  const recomputedHasInvalidPublisher = computeHasInvalidPublisherForAdSave(
    termsForCompute,
    effectiveLineItemPublishers
  );
  const hasInvalidPublisher = createAdNode.hasInvalidPublisher ?? recomputedHasInvalidPublisher;

  const lineItemWithSortedCountries =
    createAdNode.lineItem != null
      ? {
          ...createAdNode.lineItem,
          countries:
            createAdNode.lineItem.countries != null
              ? alphaSortCountryCodes(createAdNode.lineItem.countries)
              : createAdNode.lineItem.countries,
        }
      : createAdNode.lineItem;

  const createdAd: AdsPageAdFragment = {
    ...createAdNode,
    startDate: getEarliestDateRange(newAdSchedule)?.startDate,
    endDate: getLatestDateRange(newAdSchedule)?.endDate,
    schedule: newAdSchedule,
    targetingTermValues,
    countriesTarget,
    lineItem: lineItemWithSortedCountries ?? createAdNode.lineItem,
    hasInvalidCountry,
    displayPublisherTarget,
    hasInvalidPublisher,
  };

  const newEdges: GetSavedAdsQuery['ads']['edges'] = [{ node: createdAd, __typename: 'AdEdgeV5' }];

  if (currentAds) {
    newEdges.push(...currentAds.ads.edges);
  }

  const updatedCache: DataProxy.WriteQueryOptions<GetSavedAdsQuery, GetSavedAdsQueryVariables> = {
    query: GET_SAVED_ADS,
    variables: {
      lineItemIds: [lineItemId],
      lineItemId,
      ...(selectedAdIds && { selectedAdIds }),
    },
    data: {
      ads: {
        edges: newEdges,
        __typename: 'AdsPageV5',
      },
      lineItems: {
        edges: currentAds?.lineItems.edges || [],
      },
      getTargetingTermValuesByLineItemIDV5: currentAds?.getTargetingTermValuesByLineItemIDV5 ?? [],
    },
  };

  cache.writeQuery<GetSavedAdsQuery, GetSavedAdsQueryVariables>(updatedCache);

  return createdAd;
}

/** We want to take our newly created ad and write it to the cache **/
export function writeAdToCache(
  lineItemId: string,
  adFormValues: AdFormValues,
  newAdData: CreateAdsPageAdMutation,
  lineItemCountries: string[] = [],
  selectedAdIds?: string[],
  lineItemDisplayPublisherTarget: string[] = []
): void {
  if (!newAdData?.createAd) return;

  appendCreatedAdToSavedAdsCache(
    campaignClient.cache,
    lineItemId,
    adFormValues,
    newAdData.createAd,
    lineItemCountries,
    selectedAdIds,
    lineItemDisplayPublisherTarget
  );
}
